'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';

import { getProductsTableColumns } from './products-table-columns';
import { ProductsTableActions } from './products-table-actions';
import { ProductsTableActionBar } from './products-table-action-bar';
import { ProductsTableSkeleton } from './ProductsTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '../../../../../config/data-table';
import { Product } from '@tagadapay/crm-entities';
import { DeleteProductModal } from './delete-product-modal';
import { ProductImageLightbox } from '@/components/products/ProductImageLightbox';
import {
  buildProductImageGallery,
  galleryIndexForProduct,
} from '@/components/products/productImageGallery.utils';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

interface ProductsTableProps {
  products: Product.Product[];
  total: number;
  loading: boolean;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch: () => void;
  displayCurrency?: string;
  config?: TableConfig;
}

/**
 * ProductsTable component for displaying products with DataTable functionality
 */
export function ProductsTable({
  products,
  total,
  loading,
  onTableStateChange,
  onRefetch,
  displayCurrency,
  config = {},
}: ProductsTableProps) {
  const { t, i18n } = useTranslation('products');
  const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
  const [productId, setProductId] = React.useState<string | undefined>(undefined);
  const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);

  const galleryItems = React.useMemo(() => buildProductImageGallery(products), [products]);

  const openProductImage = React.useCallback(
    (id: string) => {
      const index = galleryIndexForProduct(galleryItems, id);
      if (index >= 0) setPreviewIndex(index);
    },
    [galleryItems],
  );

  const columns = React.useMemo(
    () =>
      getProductsTableColumns({
        storeId: storeId,
        onEditProduct: (id: string) => navigate(storeRoutes.PRODUCT_EDIT(id)),
        onDeleteProduct: (id: string) => setProductId(id),
        onOpenProductImage: openProductImage,
        t,
        storeRoutes,
        displayCurrency,
      }),
    [storeId, navigate, storeRoutes, t, i18n.language, displayCurrency, openProductImage],
  );

  const { table } = useDataTable({
    data: products,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: Product.Product) => row.id,
  });

  return (
    <DataTable
      key={i18n.language}
      table={table}
      actionBar={<ProductsTableActionBar table={table} />}
      isLoading={loading}
      skeletonRows={<ProductsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
    >
      <ProductsTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={storeId} />
      <DataTableSortList table={table} align="end" />
      <DeleteProductModal productId={productId} onClose={() => setProductId(undefined)} />
      <ProductImageLightbox
        items={galleryItems}
        index={previewIndex ?? 0}
        open={previewIndex !== null}
        onOpenChange={(open) => !open && setPreviewIndex(null)}
        onIndexChange={setPreviewIndex}
        prevLabel={t('table.imagePreview.previous')}
        nextLabel={t('table.imagePreview.next')}
        counterLabel={(current, total) => t('table.imagePreview.counter', { current, total })}
      />
    </DataTable>
  );
}
